import { useEffect, useMemo, useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, connect } from "react-redux";
import { SiweMessage } from "siwe";
import { getExpirationDate, validExpiration } from "@/helpers/date_helper";
import { selectUserAddress, selectChainId, selectKycSignature } from "@/store/user/selectors";
import { kycIframe } from "@/config";

function KycIframeModal({ show, onHide, userAddress, chainId, kycSignature }) {
  const iframeRef = useRef(null);
  const [heightPx, setHeightPx] = useState(620);
  const dispatch = useDispatch();

  const authAskedRef = useRef(false);

  const computedSrc = useMemo(() => {
    const base = kycIframe.url;
    return `${base}/?embed=1&open=1`;
  }, []);

  const iframeOrigin = useMemo(() => new URL(computedSrc, window.location.href).origin, [computedSrc]);
  const allowAnyOrigin = kycIframe.allowAnyOrigin;

  const isTrustedEventHost = useCallback(
    (ev) => {
      if (ev?.source !== iframeRef.current?.contentWindow) return false;
      return allowAnyOrigin ? true : ev?.origin === iframeOrigin;
    },
    [allowAnyOrigin, iframeOrigin]
  );

  const postToIframe = useCallback(
    (payload) => {
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      win.postMessage(payload, allowAnyOrigin ? "*" : iframeOrigin);
    },
    [allowAnyOrigin, iframeOrigin]
  );

  const trySendStoredSiwe = useCallback(() => {
    if (!kycSignature) return false;
    if (!validExpiration(kycSignature.expiration)) return false;
    if (kycSignature.address.toLowerCase() !== userAddress.toLowerCase()) return false;
    if (kycSignature.chainId !== chainId) return false;

    postToIframe({
      type: "kyc:siwe",
      address: kycSignature.address,
      chainId: kycSignature.chainId,
      message: kycSignature.message,
      signature: kycSignature.signature,
    });
    return true;
  }, [kycSignature, userAddress, chainId, postToIframe]);

  const requestSiweSignature = useCallback(() => {
    if (!userAddress || !chainId) return;
    if (authAskedRef.current) return;
    authAskedRef.current = true;

    const expiration = getExpirationDate();
    const msg = new SiweMessage({
      domain: window.location.host,
      address: userAddress,
      statement: "Sign in to Ensuro KYC (via host)",
      uri: window.location.origin,
      version: "1",
      chainId: Number(chainId),
      nonce: window.crypto?.getRandomValues
        ? String(window.crypto.getRandomValues(new Uint32Array(1))[0])
        : String(Math.floor(Math.random() * 1e9)),
      issuedAt: new Date().toISOString(),
      expirationTime: expiration,
    });

    const prepared = msg.prepareMessage();

    dispatch({ type: "STORE_USER_KYC", key: "kycHost", userAddress, args: {} });

    dispatch({
      type: "ETH_PLAIN_SIGN",
      key: "kycHost",
      message: prepared,
      userAddress,
    });
  }, [dispatch, userAddress, chainId]);

  useEffect(() => {
    if (!kycSignature) return;
    if (kycSignature.address.toLowerCase() !== userAddress.toLowerCase()) return;
    if (kycSignature.chainId !== chainId) return;

    postToIframe({
      type: "kyc:siwe",
      address: kycSignature.address,
      chainId: kycSignature.chainId,
      message: kycSignature.message,
      signature: kycSignature.signature,
    });

    authAskedRef.current = false;
  }, [kycSignature, userAddress, chainId, postToIframe]);

  const onMessage = useCallback(
    (ev) => {
      if (!isTrustedEventHost(ev)) return;

      const data = ev?.data;
      if (!data || typeof data !== "object") return;

      switch (data.type) {
        case "kyc:ready": {
          const reused = trySendStoredSiwe();
          if (!reused) requestSiweSignature();
          break;
        }
        case "kyc:resize": {
          const desired = Number(data.height) || 0;
          const cap = Math.floor(window.innerHeight * 0.96);
          const next = Math.min(Math.max(desired, 360), cap);
          setHeightPx(next);
          break;
        }
        case "kyc:close": {
          onHide?.();
          break;
        }
        default:
          break;
      }
    },
    [isTrustedEventHost, trySendStoredSiwe, requestSiweSignature, onHide]
  );

  useEffect(() => {
    if (!show) return;
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [show, onMessage]);

  useEffect(() => {
    if (!show) return;
    setHeightPx(620);
    authAskedRef.current = false;
  }, [show]);

  const stop = (e) => e.stopPropagation();
  if (!show) return null;

  return (
    <div
      onClick={onHide}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={stop}
        style={{
          width: "min(920px, 95vw)",
          height: heightPx,
          background: "#fff",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
          position: "relative",
        }}
      >
        <button
          onClick={onHide}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 32,
            height: 32,
            border: 0,
            borderRadius: 6,
            background: "rgba(0,0,0,0.06)",
            cursor: "pointer",
          }}
        >
          ✕
        </button>
        <iframe
          ref={iframeRef}
          title="Ensuro KYC"
          src={computedSrc}
          style={{ width: "100%", height: "100%", border: 0 }}
          sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    </div>
  );
}

KycIframeModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  userAddress: PropTypes.string,
  chainId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  kycSignature: PropTypes.object,
};

const mapStateToProps = (state) => ({
  userAddress: selectUserAddress(state.UserReducer),
  chainId: selectChainId(state.UserReducer),
  kycSignature: selectKycSignature(state.UserReducer),
});

export default connect(mapStateToProps)(KycIframeModal);
